/* eslint-disable jsx-a11y/anchor-is-valid */
import styles from "./SalesTransactions.module.scss";
import secondaryComponents from "../../../../components/secondaryComponents";
import primaryComponents from "../../../../components/primaryComponents";
import { useSalesTransactions } from "./useSalesTransactions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import globalStyles from "../../../../assets/styles/base/globalPage.module.scss";
import { ReturnIcon } from "../../../../assets/svgIcons";
import { orderStatus, returnOrderStatus } from "../../../../localdata";
import { useEffect } from "react";
import { pathConstants } from "../../../../routes/pathContants";
import { useNavigate } from "react-router-dom";
import { RoutedModal } from "../../../../components/common";

const SalesTransactions = () => {
  const navigate = useNavigate();
  const {
    isFetchingSalesTransactions,
    storeList,
    currentStore,
    metaDetails,
    salesTransactions,
    search,
    userList,
    isLoadingUsers,
    isModal,
    props,
    setProps,
    filterByDate,
    setIsModal,
    filterByInitiator,
    handleFetchSalesTransactions,
    filterByStoreId,
    gotoPage,
    filterBySearch,
    filterByStatus,
    filterByStoreType,
    filterReturnStatus,
    filterByUnsynced,
  } = useSalesTransactions({});

  const { role: business_role } = useSelector(
    (state) => state.profileDetailsReducer.profileDetails,
  );

  const { has_chowdeck_connection, has_squareup_connection } = useSelector(
    (state) => state.profileDetailsReducer.businessDetails,
  );
  const isWayBillUser = business_role === "WAYBILL_MANAGER";

  useEffect(() => {
    if (!search.get("order_source")) filterByStoreType("INTERNAL");
  }, []);

  return (
    <secondaryComponents.MetaSetter
      title="Lumi Merchant | Inventory - Sales Transactions"
      description="View all sales records"
    >
      <secondaryComponents.RequestLoaderTrigger>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.3 } }}
          exit={{ opacity: 0 }}
          className={`${styles.salesTransactions} ${globalStyles.pageColumnGaps}`}
        >
          <div>
            <h4 className="mb-0">Sales Transaction</h4>
            <p>
              View, filter, and generate reports and invoices for your sales
              transactions.
            </p>
          </div>

          <div className={globalStyles.rowNavigations}>
            <a
              className={`${globalStyles.rowNavigations__item} ${
                search.get("order_source") === "INTERNAL" &&
                !search.get("unsynced")
                  ? globalStyles.active
                  : ""
              }`}
              onClick={() => filterByStoreType("INTERNAL", true)}
            >
              Instore Sales
            </a>

            <secondaryComponents.NavigateWithPermission
              permissionParentKey={"Inventory Management"}
            >
              <a
                className={`${globalStyles.rowNavigations__item} ${
                  search.get("order_source") === "STOREFRONT" &&
                  !search.get("unsynced")
                    ? globalStyles.active
                    : ""
                }`}
                onClick={() => filterByStoreType("STOREFRONT", true)}
              >
                Online Orders
              </a>
            </secondaryComponents.NavigateWithPermission>

            {has_chowdeck_connection ? (
              <a
                className={`${globalStyles.rowNavigations__item} ${
                  search.get("order_source") === "CHOWDECK" &&
                  !search.get("unsynced")
                    ? globalStyles.active
                    : ""
                }`}
                onClick={() => filterByStoreType("CHOWDECK", true)}
              >
                Chowdeck Orders
              </a>
            ) : null}

            {has_squareup_connection ? (
              <a
                className={`${globalStyles.rowNavigations__item} ${
                  search.get("order_source") === "SQUAREUP" &&
                  !search.get("unsynced")
                    ? globalStyles.active
                    : ""
                }`}
                onClick={() => filterByStoreType("SQUAREUP", true)}
              >
                Squareup Orders
              </a>
            ) : null}

            {process.env.REACT_APP_ENV === "localSetup" ? (
              <a
                className={`${globalStyles.rowNavigations__item} ${
                  search.get("order_source") === "INTERNAL" &&
                  search.get("unsynced") === "true"
                    ? globalStyles.active
                    : ""
                }`}
                onClick={() => filterByUnsynced(true)}
              >
                Unsync Sales
              </a>
            ) : null}
          </div>
          <div className={styles.salesTransactions__header}>
            <div style={{ width: "200px" }}>
              <secondaryComponents.LoaderHelper isLoading={isLoadingUsers}>
                <primaryComponents.SelectField
                  options={[
                    { first_name: "All Initiators", id: null },
                    ...userList,
                  ]}
                  idKey="id"
                  nameKey="first_name"
                  onChange={(value) => {
                    filterByInitiator(value);
                  }}
                  value={
                    userList?.find(
                      (item) => `${item?.id}` === search.get("initiator_id"),
                    )?.first_name || "Select Initiator"
                  }
                  floatError={false}
                  classNames="store-select"
                />
              </secondaryComponents.LoaderHelper>
            </div>
            <primaryComponents.DateRangeInput
              startDate={search.get("start_date")}
              endDate={search.get("end_date")}
              onChange={filterByDate}
              classNames=""
            />
            <secondaryComponents.NavigateWithPermission
              permissionParentKey={"Inventory Management"}
              permissionChildKey="can_generate_end_of_day"
              showOnLocalSetup={true}
            >
              <primaryComponents.Button
                classNames="btn btn--outline"
                onClick={() =>
                  setIsModal((prev) => ({ ...prev, isEndOfDay: true }))
                }
              >
                <FontAwesomeIcon icon="download" className="mr-2" />
                Export End Of Day
              </primaryComponents.Button>
            </secondaryComponents.NavigateWithPermission>
            <secondaryComponents.NavigateWithPermission
              permissionParentKey={"Inventory Management"}
              permissionChildKey="can_view_returns"
            >
              <primaryComponents.Button
                classNames="btn btn--orange"
                onClick={() => navigate(pathConstants.INVENTORY_RETURNS)}
              >
                <ReturnIcon />
                <span className="ml-2">Returns</span>
              </primaryComponents.Button>
            </secondaryComponents.NavigateWithPermission>
          </div>

          <div className={styles.salesTransactions__content}>
            <div className={globalStyles.tableHeader}>
              <primaryComponents.SearchField
                classNames={globalStyles.searchField}
                placeholder="Search for a transaction by (Order Number, Transaction Ref)"
                trigger={(value) => filterBySearch(value)}
                value={search.get("query") !== null ? search.get("query") : ""}
                triggerOnInput={true}
                showsearchBtn={true}
              />
              <div style={{ width: "160px" }}>
                <primaryComponents.SelectField
                  options={[
                    {
                      name: "All stores",
                      id: null,
                    },
                    ...storeList,
                  ]}
                  idKey="id"
                  nameKey="name"
                  onChange={(value) => {
                    filterByStoreId(value);
                  }}
                  value={
                    storeList?.find(
                      (storeItem) => `${storeItem?.id}` === `${currentStore}`,
                    )?.name || "All stores"
                  }
                  floatError={false}
                  classNames="store-select"
                />
              </div>
              <div style={{ width: "160px" }}>
                <primaryComponents.SelectField
                  value={
                    orderStatus?.find(
                      (status) => status?.value === search.get("status"),
                    )?.label || "Select status"
                  }
                  options={orderStatus}
                  onChange={(value) => {
                    filterByStatus(value);
                  }}
                  nameKey="label"
                  idKey="value"
                  floatError={false}
                />
              </div>
              <div style={{ width: "170px" }}>
                <primaryComponents.SelectField
                  value={
                    returnOrderStatus?.find(
                      (status) => status?.value === search.get("return_status"),
                    )?.label || "Select return status"
                  }
                  options={returnOrderStatus}
                  onChange={(value) => {
                    filterReturnStatus(value);
                  }}
                  nameKey="label"
                  idKey="value"
                  floatError={false}
                />
              </div>
            </div>

            <secondaryComponents.LoaderHelper
              isLoading={isFetchingSalesTransactions}
              classNames="mt-5"
            >
              <secondaryComponents.SalesTransactionsTable
                metaDetails={metaDetails}
                salesTransactions={salesTransactions}
                handleFetchSalesTransactions={handleFetchSalesTransactions}
                gotoPage={gotoPage}
                showpricing={!isWayBillUser}
              />
            </secondaryComponents.LoaderHelper>
          </div>

          {/* export end of day widget */}
          <RoutedModal
            isActive={isModal?.isEndOfDay}
            headerTitle={props?.headerTitle}
            headerDescription={props?.headerDescription}
            hasPrevNavigation={props?.hasPrevNavigation}
            routeToPrev={props?.routeToPrev}
            closeModal={() =>
              setIsModal((prev) => ({ ...prev, isEndOfDay: false }))
            }
            bodyStyle={{ paddingBottom: "0" }}
            style={{ paddingBottom: "0" }}
          >
            <secondaryComponents.SalesEndOfDay
              props={props}
              setProps={setProps}
            />
          </RoutedModal>

          {/* return widget */}
          <secondaryComponents.SideModal
            isActive={isModal?.isReturn}
            closeModal={() =>
              setIsModal((prev) => ({ ...prev, isReturn: false }))
            }
          >
            <secondaryComponents.ReturnStockWidget
              closeWidget={() =>
                setIsModal((prev) => ({ ...prev, isReturn: false }))
              }
            />
          </secondaryComponents.SideModal>
        </motion.div>
      </secondaryComponents.RequestLoaderTrigger>
    </secondaryComponents.MetaSetter>
  );
};

export default SalesTransactions;
